import React, { FC, useEffect, createContext, useRef, useState, useContext } from 'react'
import { useInterval } from 'common/hooks/useInterval'
import { useDispatch, useSelector } from 'react-redux'
import { isWeekdayAWST, isBetweenUTC } from 'common/helpers/time'
import { loadStationStatus, TheWestLiveState } from 'common/data/west-live/reducer'
import { AppState } from 'common/store'
import { LoggerContext } from 'component-library/diagnostics/LoggerContext'
import { RenderTargetContext } from 'common/context-providers/render-target-context'

interface HoursWindow {
    startTime: [number, number]
    endTime: [number, number]
}
interface TheWestLiveProviderProps extends HoursWindow {
    stationId?: string
}

export interface TheWestLiveContextProps extends Omit<TheWestLiveProviderProps, 'stationId'> {
    playerState: TheWestLivePlayerState
    playerActions: {
        onControlClick: () => void
        onVolumeChange: (volume: number) => void
    }
}

export type TheWestLivePlayerStates = 'playing' | 'loading' | 'paused'

export interface TheWestLivePlayerState {
    state: TheWestLivePlayerStates
    volume: number
}

export const TheWestLiveContext = createContext<TheWestLiveContextProps>({
    startTime: [0, 0],
    endTime: [0, 0],
    playerState: {
        state: 'loading',
        volume: 0.5,
    },
    playerActions: {
        onControlClick: () => null,
        onVolumeChange: () => null,
    },
})

export const TheWestLiveProvider: FC<TheWestLiveProviderProps> = ({
    stationId,
    startTime,
    endTime,
    children,
}) => {
    const dispatch = useDispatch()
    const logger = useContext(LoggerContext)
    const { renderTarget } = React.useContext(RenderTargetContext)
    const streamingHostname = useSelector((state: AppState) => state.theWestLive.streamingHostname)

    // Our player displays 5 minutes either side of the schedule
    const withinSchedule = withinScheduledWindow({ startTime, endTime })

    useEffect(() => {
        if (withinSchedule && stationId) {
            dispatch(loadStationStatus(stationId))
        }
    }, [stationId, dispatch, withinSchedule])

    useInterval(() => {
        // If we're inside the scheduled window, check for on-air status every 30s
        if (withinSchedule && stationId) {
            dispatch(loadStationStatus(stationId))
        }
    }, 30_000)

    const playerRef = useRef<HTMLAudioElement>(null)

    const [playerState, setPlayerState] = useState<TheWestLivePlayerState>({
        state: 'loading',
        volume: playerRef.current?.volume || 0.5,
    })

    const onControlClickHandler = () => {
        if (playerRef.current?.paused) {
            playerRef.current?.play()
            setPlayerState({ ...playerState, state: 'playing' })
        } else {
            playerRef.current?.pause()
        }
    }

    const onCanPlayHandler = () => {
        logger.debug('Player can play')
        if (playerState.state === 'loading') {
            setPlayerState({ ...playerState, state: 'paused' })
        }
    }

    const onPlayingHandler = () => {
        logger.debug('Player playing')
        setPlayerState({ ...playerState, state: 'playing' })
    }

    const onPauseHandler = () => {
        logger.debug('Player paused')
        setPlayerState({ ...playerState, state: 'paused' })
    }

    const onSuspendHandler = () => {
        logger.debug('Player suspended')
    }

    const onStalledHandler = () => {
        logger.debug('Player stalled')
    }

    const onWaitingHandler = () => {
        logger.debug('Player waiting')
    }

    const volumeChangeHandler = (volume: number) => {
        setPlayerState({ ...playerState, volume })
        if (playerRef.current) {
            playerRef.current.volume = volume
        }
    }

    const onErrorHandler = (event: React.SyntheticEvent<HTMLAudioElement, Event>) => {
        logger.error(event, `Player error: ${event.type}`)
    }

    const streamUrl =
        streamingHostname && stationId
            ? `http://${streamingHostname}/${stationId}/listen`
            : undefined

    const playerElement = (
        <audio
            ref={playerRef}
            onPlaying={onPlayingHandler}
            onPause={onPauseHandler}
            onSuspend={onSuspendHandler}
            onCanPlay={onCanPlayHandler}
            onStalled={onStalledHandler}
            onWaiting={onWaitingHandler}
            onError={onErrorHandler}
        >
            {streamUrl && <source src={streamUrl} />}
        </audio>
    )

    const context: TheWestLiveContextProps = {
        startTime,
        endTime,
        playerState,
        playerActions: {
            onControlClick: onControlClickHandler,
            onVolumeChange: volumeChangeHandler,
        },
    }

    return (
        <TheWestLiveContext.Provider value={context}>
            {renderTarget === 'web' && playerElement}
            {children}
        </TheWestLiveContext.Provider>
    )
}

export const queryOverrideOffAir =
    typeof window !== 'undefined' && window.location.search.indexOf('the_west_live=offair') !== -1

/**
 * Check west live schedule override
 * ?the_west_live query will enable the west live schedule and set window.theWestLive=true to persist the state for navigation
 * settting window.theWestLive directly requires a rerender / navigation
 */
function scheduleOverride() {
    const maybeWindow = typeof window !== 'undefined' ? (window as any) : undefined
    if (maybeWindow && maybeWindow.location.search.indexOf('the_west_live') !== -1) {
        maybeWindow.theWestLive = true
    }
    return maybeWindow && maybeWindow.theWestLive
}

export function withinScheduledWindow(window: HoursWindow): boolean {
    return (
        scheduleOverride() ||
        (isWeekdayAWST() &&
            isBetweenUTC(
                new Date(),
                [window.startTime[0], window.startTime[1] - 5],
                [window.endTime[0], window.endTime[1] + 5]
            ))
    )
}

export function useScheduleWindow(): boolean {
    const { startTime, endTime } = useContext(TheWestLiveContext)
    return withinScheduledWindow({ startTime, endTime })
}

export function useOnAirState(): boolean {
    return useSelector<AppState, boolean>(state => state.theWestLive.onAir) && !queryOverrideOffAir
}

export function useStationStatus(): TheWestLiveState['status'] {
    return useSelector<AppState, TheWestLiveState['status']>(state => state.theWestLive.status)
}
